import { useEffect, useState, useCallback } from 'react'
import { useParams, Link } from '@tanstack/react-router'
import {
  Users,
  MapPin,
  Building2,
  Calendar,
  ArrowLeft,
  Accessibility,
} from 'lucide-react'
import { api, type Room, type PublicOrg, type RoomSchedule, type PublicBuilding } from '../lib/api'
import { RichTextContent } from '../components/RichTextEditor'
import { MarketplaceLayout } from '../components/MarketplaceLayout'
import { useAuthStore } from '../lib/auth'
import { UnifiedBookingModal, type BookingSubmitData } from '../components/UnifiedBookingModal'
import {
  getAccessibilityFeature,
  ACCESSIBILITY_CATEGORY_LABELS,
  getAccessibilityCategories,
} from '@roomsi/shared'
import Lightbox from 'yet-another-react-lightbox'
import 'yet-another-react-lightbox/styles.css'

const CURRENCY_SYMBOLS: Record<string, string> = {
  USD: '$',
  EUR: '€',
  GBP: '£',
  AUD: 'A$',
  CAD: 'C$',
  NZD: 'NZ$',
  JPY: '¥',
  CHF: 'CHF',
  SGD: 'S$',
  HKD: 'HK$',
}

const formatPrice = (price: number | string | null, currency = 'GBP') => {
  if (price === null) return 'Price on request'
  const numPrice = typeof price === 'string' ? parseFloat(price) : price
  if (isNaN(numPrice)) return 'Price on request'
  const symbol = CURRENCY_SYMBOLS[currency] || currency
  return `${symbol}${numPrice.toFixed(2)}`
}

export function MarketplaceRoomPage() {
  const { orgSlug, roomSlug } = useParams({ from: '/marketplace/$orgSlug/$roomSlug' })
  const { isAuthenticated, user, memberships, checkAuth, setCurrentOrg } = useAuthStore()

  // Room data
  const [organisation, setOrganisation] = useState<PublicOrg | null>(null)
  const [room, setRoom] = useState<Room | null>(null)
  const [building, setBuilding] = useState<PublicBuilding | null>(null)
  const [schedules, setSchedules] = useState<RoomSchedule[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')

  // Image gallery
  const [activeImageIndex, setActiveImageIndex] = useState(0)
  const [lightboxOpen, setLightboxOpen] = useState(false)

  // Booking modal state
  const [showBookingModal, setShowBookingModal] = useState(false)

  // Check if user is a member of this org
  const membership = memberships.find((m) => m.orgSlug === orgSlug)
  const isMember = !!membership && membership.status === 'active'

  // Determine if org allows public bookings
  const allowsPublicBookings = organisation?.allowPublicBookings ?? false

  useEffect(() => {
    checkAuth()
  }, [])

  useEffect(() => {
    loadRoom()
  }, [orgSlug, roomSlug])

  // Update page title when room loads
  useEffect(() => {
    if (room && organisation) {
      document.title = `${room.name} - ${organisation.name} | Roomsi`

      // Set meta description
      const description = room.descriptionHtml
        ? room.descriptionHtml.replace(/<[^>]*>/g, '').slice(0, 155) + '...'
        : `Book ${room.name} at ${organisation.name}. Capacity: ${room.capacity} guests.`
      setMetaTag('description', description)

      // Open Graph tags
      setMetaTag('og:title', `${room.name} - ${organisation.name}`)
      setMetaTag('og:description', description)
      setMetaTag('og:type', 'place')
      setMetaTag('og:url', window.location.href)
      if (room.images && room.images.length > 0) {
        const heroImg = room.images[room.heroImageIndex || 0] || room.images[0]
        if (heroImg?.url) setMetaTag('og:image', heroImg.url)
      }

      // Twitter Card tags
      setMetaTag('twitter:card', 'summary_large_image')
      setMetaTag('twitter:title', `${room.name} - ${organisation.name}`)
      setMetaTag('twitter:description', description)
      if (room.images && room.images.length > 0) {
        const heroImg = room.images[room.heroImageIndex || 0] || room.images[0]
        if (heroImg?.url) setMetaTag('twitter:image', heroImg.url)
      }
    }
    return () => {
      document.title = 'Roomsi'
    }
  }, [room, organisation])

  // Inject Schema.org JSON-LD when room data loads
  useEffect(() => {
    if (room && organisation) {
      injectRoomSchemaOrg(room, organisation, building, schedules)
    }
    return () => {
      const existingSchema = document.querySelector('script[data-schema="room"]')
      if (existingSchema) existingSchema.remove()
    }
  }, [room, organisation, building, schedules])

  const setMetaTag = (name: string, content: string) => {
    const isOg = name.startsWith('og:')
    const isTwitter = name.startsWith('twitter:')
    const attr = isOg || isTwitter ? 'property' : 'name'

    let tag = document.querySelector(`meta[${attr}="${name}"]`)
    if (!tag) {
      tag = document.createElement('meta')
      tag.setAttribute(attr, name)
      document.head.appendChild(tag)
    }
    tag.setAttribute('content', content)
  }

  const injectRoomSchemaOrg = (
    room: Room,
    org: PublicOrg,
    building: PublicBuilding | null,
    schedules: RoomSchedule[]
  ) => {
    // Remove existing schema
    const existingSchema = document.querySelector('script[data-schema="room"]')
    if (existingSchema) existingSchema.remove()

    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

    const schema: Record<string, unknown> = {
      '@context': 'https://schema.org',
      '@type': 'MeetingRoom',
      name: room.name,
      description:
        room.descriptionHtml?.replace(/<[^>]*>/g, '').slice(0, 500) ||
        `Meeting room at ${org.name}`,
      url: window.location.href,
      maximumAttendeeCapacity: room.capacity,
      containedInPlace: {
        '@type': 'LocalBusiness',
        name: org.name,
        url: `${window.location.origin}/marketplace/${org.slug}`,
      },
    }

    // Add images
    if (room.images && room.images.length > 0) {
      schema.image = room.images.map((img) => img.url)
    }

    // Add price
    if (room.hourlyRate) {
      schema.priceRange = `${CURRENCY_SYMBOLS[org.currency] || org.currency}${room.hourlyRate}/hr`
    }

    // Add location if building exists
    if (building) {
      schema.address = {
        '@type': 'PostalAddress',
        streetAddress: building.address,
      }
    }

    // Add opening hours from schedules
    if (schedules && schedules.length > 0) {
      schema.openingHoursSpecification = schedules.map((s) => ({
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: dayNames[s.dayOfWeek],
        opens: s.openTime,
        closes: s.closeTime,
      }))
    }

    // Add amenities as features
    if (room.amenities && room.amenities.length > 0) {
      schema.amenityFeature = room.amenities.map((a) => ({
        '@type': 'LocationFeatureSpecification',
        name: a,
        value: true,
      }))
    }

    const script = document.createElement('script')
    script.type = 'application/ld+json'
    script.setAttribute('data-schema', 'room')
    script.textContent = JSON.stringify(schema)
    document.head.appendChild(script)
  }

  // Fetch availability callback for UnifiedBookingModal
  const fetchAvailability = useCallback(
    async (_roomId: string, startDate: string, endDate: string) => {
      try {
        const result = await api.public.getRoomAvailability(orgSlug, roomSlug, startDate, endDate)
        return result.bookings
      } catch (err) {
        console.error('Failed to load availability:', err)
        return []
      }
    },
    [orgSlug, roomSlug]
  )

  const loadRoom = async () => {
    try {
      setIsLoading(true)
      setError('')
      const data = await api.public.getRoom(orgSlug, roomSlug)
      setOrganisation(data.organisation)
      setRoom(data.room)
      setActiveImageIndex(data.room.heroImageIndex || 0)
      setBuilding(data.building || null)
      setSchedules(data.schedules || [])
    } catch (err: unknown) {
      if (err instanceof Error && 'code' in err && err.code === 'NOT_FOUND') {
        setError('Room not found')
      } else {
        setError('Failed to load room')
      }
    } finally {
      setIsLoading(false)
    }
  }

  // Format time for display (used in schedule display)
  const formatTime = (time: string) => {
    const parts = time.split(':')
    const hour = parseInt(parts[0] || '0')
    const m = parts[1] || '00'
    const ampm = hour >= 12 ? 'PM' : 'AM'
    const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour
    return `${hour12}:${m} ${ampm}`
  }

  // Determine modal mode based on authentication and membership
  const getBookingMode = (): 'member' | 'guest' | 'request' => {
    if (isAuthenticated && isMember) return 'member'
    if (allowsPublicBookings) return 'guest'
    return 'request'
  }

  // Handle booking submission from UnifiedBookingModal
  const handleBookingSubmit = async (data: BookingSubmitData) => {
    if (!room) return

    const dateStr = data.date
    const startTime = new Date(`${dateStr}T${data.startTime}:00`).toISOString()
    const endTime = new Date(`${dateStr}T${data.endTime}:00`).toISOString()

    if (isAuthenticated && isMember) {
      // Member booking
      await setCurrentOrg(orgSlug)
      const result = await api.bookings.create({
        roomId: room.id,
        startTime,
        endTime,
        localStartTime: data.startTime,
        localEndTime: data.endTime,
        title: data.title || undefined,
        notes: data.notes || undefined,
        donationAmount: data.donationAmount,
      })
      // Let the modal tailor its success message (and start a pledged donation)
      return { booking: { id: result.booking.id, status: result.booking.status } }
    } else if (allowsPublicBookings) {
      // Guest booking
      await api.public.createGuestBooking(orgSlug, room.id, {
        email: data.guestEmail!.trim(),
        name: data.guestName!.trim(),
        startTime,
        endTime,
        notes: data.notes || undefined,
      })
    } else {
      // Access request - use all selected slots for the preferred time
      const slotTimesFormatted = data.selectedSlots.map((t) => formatTime(t)).join(', ')
      // Also send raw slots (24h format) for proper parsing later
      const rawSlots = data.selectedSlots.join(',')

      const response = await fetch('/api/v1/marketplace/request-access', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          orgSlug,
          name: data.guestName?.trim(),
          email: data.guestEmail?.trim(),
          phone: data.guestPhone?.trim() || undefined,
          preferredDate: dateStr,
          preferredTime: slotTimesFormatted,
          preferredSlots: rawSlots, // Raw 24h format slots: "14:00,15:00,18:00"
          message: data.notes?.trim() || undefined,
          roomId: room.id,
          roomName: room.name,
        }),
      })

      const result = await response.json()

      if (!response.ok) {
        throw new Error(result.error || 'Failed to submit request')
      }
    }

    // Don't close modal - let UnifiedBookingModal show its success state
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  if (error || !room) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Building2 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">{error || 'Room not found'}</h1>
          <p className="text-gray-500 mb-4">
            The room you're looking for doesn't exist or isn't public.
          </p>
          <Link
            to="/marketplace/$orgSlug"
            params={{ orgSlug }}
            className="text-primary-600 hover:text-primary-700"
          >
            Back to {organisation?.name || 'organisation'}
          </Link>
        </div>
      </div>
    )
  }

  return (
    <MarketplaceLayout returnTo={`/marketplace/${orgSlug}/${roomSlug}`} className="pb-24 lg:pb-0">
      {/* Main Content */}
      <main className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8 pb-28 lg:pb-12">
        <Link to="/marketplace/$orgSlug" params={{ orgSlug }} className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-5">
          <ArrowLeft className="w-4 h-4 shrink-0" />
          {organisation?.name || 'All rooms'}
        </Link>
        <h1 className="text-3xl sm:text-[40px] font-extrabold leading-tight break-words mb-6">{room.name}</h1>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-8">
          {/* Left Column - Main Content */}
          <div className="lg:col-span-2 space-y-4 lg:space-y-6">
            {/* Image Gallery */}
            <div className="bg-white rounded-2xl overflow-hidden">
              {room.images && room.images.length > 0 ? (
                <>
                  <button
                    type="button"
                    aria-label={`Show all ${room.images.length} photos of ${room.name}`}
                    className="block w-full aspect-[4/3] sm:aspect-[16/9] cursor-pointer overflow-hidden"
                    onClick={() => {
                      setLightboxOpen(true)
                    }}
                  >
                    <img
                      src={room.images[activeImageIndex]?.url}
                      alt={room.images[activeImageIndex]?.alt || room.name}
                      className="w-full h-full object-cover"
                    />
                  </button>
                  {room.images.length > 1 && (
                    <div className="p-2 flex gap-2 overflow-x-auto scrollbar-hide">
                      {room.images.map((img, idx) => (
                        <button
                          key={idx}
                          onClick={() => setActiveImageIndex(idx)}
                          aria-label={`Photo ${idx + 1}`}
                          aria-pressed={idx === activeImageIndex}
                          className={`flex-shrink-0 w-14 h-14 sm:w-16 sm:h-16 rounded-lg overflow-hidden border-2 transition-colors ${
                            idx === activeImageIndex ? 'border-gray-900' : 'border-transparent'
                          }`}
                        >
                          <img
                            src={img.url}
                            alt={img.alt || ''}
                            className="w-full h-full object-cover"
                          />
                        </button>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <div className="aspect-[4/3] sm:aspect-[16/9] bg-gray-100 flex items-center justify-center">
                  <Building2 className="w-16 h-16 text-gray-300" />
                </div>
              )}
            </div>

            {/* Room Details */}
            <section className="border-b border-border py-6">
              {/* Mobile: Stack name and price vertically */}
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-4">
                <div className="flex-1 min-w-0">
                  <h2 className="text-2xl font-bold text-gray-900">About this room</h2>
                  {building && (
                    <p className="text-gray-600 flex items-start gap-1.5 mt-2 text-sm">
                      <MapPin className="w-4 h-4 flex-shrink-0 mt-0.5" />
                      <span>
                        {building.name}
                        {building.address && `, ${building.address}`}
                      </span>
                    </p>
                  )}
                </div>
                {/* Show price inline on mobile, hidden on desktop (shown in booking card) */}
                <div className="text-left sm:text-right lg:hidden">
                  <div className="text-xl font-semibold text-gray-900">
                    {formatPrice(room.hourlyRate, organisation?.currency)}
                    <span className="text-sm font-normal text-gray-600"> / hour</span>
                  </div>
                </div>
                {/* Desktop price - hidden on mobile/tablet */}
                <div className="hidden lg:block text-right">
                  <div className="text-xl font-semibold text-gray-900">
                    {formatPrice(room.hourlyRate, organisation?.currency)}
                    <span className="text-sm font-normal text-gray-600"> / hour</span>
                  </div>
                </div>
              </div>

              {/* Capacity badge */}
              <div className="flex flex-wrap gap-3 mb-5">
                <div className="flex items-center gap-2 text-gray-600 bg-gray-50 px-3 py-1.5 rounded-full text-sm">
                  <Users className="w-4 h-4" />
                  <span>Up to {room.capacity} people</span>
                </div>
              </div>

              {/* Accessibility Features - Show prominently on mobile */}
              {room.accessibilityFeatures && room.accessibilityFeatures.length > 0 && (
                <div className="mb-6 border-t border-border pt-5">
                  <h3 className="text-sm font-semibold text-gray-900 mb-2.5 flex items-center gap-2">
                    <Accessibility className="w-4 h-4 text-community-strong" />
                    Accessibility
                  </h3>
                  <div className="flex flex-wrap gap-1.5 sm:gap-2">
                    {room.accessibilityFeatures.map((featureId, idx) => {
                      const feature = getAccessibilityFeature(featureId)
                      return (
                        <span
                          key={idx}
                          className="px-2.5 py-1 bg-community-soft text-community-strong rounded-full text-sm"
                          title={feature?.description}
                        >
                          {feature?.label || featureId}
                        </span>
                      )
                    })}
                  </div>
                  {room.accessibilityNotes && (
                    <p className="mt-2.5 text-sm text-gray-600">{room.accessibilityNotes}</p>
                  )}
                </div>
              )}

              {/* Amenities */}
              {room.amenities && room.amenities.length > 0 && (
                <div className="mb-5">
                  <h3 className="text-sm font-semibold text-gray-900 mb-2">Amenities</h3>
                  <div className="flex flex-wrap gap-1.5 sm:gap-2">
                    {room.amenities.map((amenity, idx) => (
                      <span
                        key={idx}
                        className="px-2.5 py-1 bg-gray-100 text-gray-700 rounded-full text-xs sm:text-sm"
                      >
                        {amenity}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {room.descriptionHtml && (
                <div className="prose prose-sm max-w-none">
                  <RichTextContent content={room.descriptionHtml} />
                </div>
              )}
            </section>

            {/* Operating Hours */}
            {schedules.length > 0 && (
              <section className="py-4">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">
                  Opening hours
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 xl:grid-cols-7 gap-2">
                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, index) => {
                    const schedule = schedules.find((s) => s.dayOfWeek === index)
                    return (
                      <div
                        key={day}
                        className={`min-h-20 p-3 rounded-md text-center ${schedule?.isActive ? 'bg-community-soft' : 'bg-gray-50'}`}
                      >
                        <div className="text-xs font-semibold text-gray-600 mb-1">
                          {day}
                        </div>
                        <div
                          className={`text-xs leading-relaxed ${schedule?.isActive ? 'text-community-strong font-medium' : 'text-gray-600'}`}
                        >
                          {schedule?.isActive ? (
                            <span className="block">
                              {schedule.openTime.slice(0, 5)} - {schedule.closeTime.slice(0, 5)}
                            </span>
                          ) : (
                            'Closed'
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>
              </section>
            )}
          </div>

          {/* Right Column - Desktop Booking CTA */}
          <div className="hidden lg:block space-y-4 lg:space-y-6">
            {/* Quick Booking Card - Desktop */}
            <div className="bg-white border border-gray-200 rounded-2xl p-6 sticky top-28 shadow-lg">
              <div className="text-center mb-4">
                <div className="text-2xl font-semibold text-gray-900 mb-1">
                  {formatPrice(room.hourlyRate, organisation?.currency)}
                </div>
                <p className="text-sm text-gray-500">per hour</p>
              </div>

              <button
                onClick={() => setShowBookingModal(true)}
                className="w-full py-3 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-semibold transition-colors flex items-center justify-center gap-2"
              >
                <Calendar className="w-5 h-5" />
                {allowsPublicBookings || isMember ? 'Book this room' : 'Request to book'}
              </button>

              {/* Quick info */}
              <div className="mt-4 pt-4 border-t border-gray-100 space-y-2">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Users className="w-4 h-4" />
                  <span>Fits up to {room.capacity} people</span>
                </div>
                {building?.address && (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <MapPin className="w-4 h-4" />
                    <span className="break-words">{building.address}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Mobile Fixed Bottom Booking Bar */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-3 sm:p-4 z-40 shadow-lg">
        <div className="flex items-center gap-3 max-w-lg mx-auto">
          <div className="flex-1 min-w-0">
            <div className="text-lg sm:text-xl font-bold text-gray-900">
              {formatPrice(room.hourlyRate, organisation?.currency)}
            </div>
            <p className="text-xs text-gray-500 truncate">per hour</p>
          </div>
          <button
            onClick={() => setShowBookingModal(true)}
            className="px-5 sm:px-6 py-2.5 sm:py-3 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-semibold transition-colors flex items-center gap-2 text-sm sm:text-base"
          >
            <Calendar className="w-4 h-4 sm:w-5 sm:h-5" />
            {allowsPublicBookings || isMember ? 'Book this room' : 'Request'}
          </button>
        </div>
      </div>

      {/* Booking Modal - Using UnifiedBookingModal */}
      {showBookingModal && room && (
        <UnifiedBookingModal
          room={room}
          roomName={room.name}
          schedules={schedules}
          currency={organisation?.currency || 'GBP'}
          canBookOutOfHours={membership?.canBookOutOfHours || false}
          allowOutOfHours={membership?.role === 'admin' || membership?.role === 'approver'}
          mode={getBookingMode()}
          orgSlug={orgSlug}
          userName={user?.name || ''}
          userEmail={user?.email || ''}
          onClose={() => setShowBookingModal(false)}
          onSubmit={handleBookingSubmit}
          fetchAvailability={fetchAvailability}
          submitButtonText={allowsPublicBookings || isMember ? 'Confirm booking' : 'Send request'}
          title={allowsPublicBookings || isMember ? 'Book this room' : 'Request to book'}
        />
      )}

      {/* Lightbox */}
      {room.images && room.images.length > 0 && (
        <Lightbox
          open={lightboxOpen}
          close={() => setLightboxOpen(false)}
          index={activeImageIndex}
          slides={room.images.map((img) => ({ src: img.url, alt: img.alt }))}
        />
      )}

    </MarketplaceLayout>
  )
}
